// components/slides/BrandResearchResult.tsx
import React from 'react';
import ReactMarkdown from 'react-markdown';
import type { ScriptInstance, SlideBrandResearch } from '#types/types';
import { hostnameOf } from '#lib/leasing/enrichNote';

// An agent message's text size (Chat.tsx bubbleStyle). Inline because the global
// `div, span { font-size: 12px }` rule in globals.css beats Tailwind's text-* utilities.
const MESSAGE_TEXT: React.CSSProperties = { fontSize: 13, lineHeight: 1.45 };

// Only a full date is reformatted: a bare year or month stays as the source gave it rather than becoming an
// invented day, and a date without a time is read in UTC so it does not shift a day west of Greenwich.
const formatDate = (value: string) => {
  if (!/^\d{4}-\d{2}-\d{2}/.test(value)) return value;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleDateString(undefined, value.includes('T') ? undefined : { timeZone: 'UTC' });
};

export function BrandResearchResult({ scripts }: { scripts: ScriptInstance[] }) {
  const results = scripts.flatMap((s) => s.commands).filter((c) =>
    c.action_type === 'SLIDES_SAVE_BRAND_RESEARCH' && c.status === 'success' && c.parameters?.result?.research);
  const failures = scripts.flatMap((s) => s.commands).filter((c) => c.action_type === 'SLIDES_WEB_SEARCH' && c.parameters?.results?.notice);
  return <>{failures.map((c) => <p key={c.id} role="status" className="my-2 text-xs text-[#B8B8B8]">{String(c.parameters.results.notice)}</p>)}{results.map((command) => {
    const research = command.parameters.result.research as SlideBrandResearch;
    return <section key={command.id} aria-label="Brand research results" className="chat-message-content-area my-3 min-w-0 text-[#D0D0D0]" style={MESSAGE_TEXT}>
      <p className="mb-2 text-[#999999]">{research.brand.name} · {formatDate(research.researchedAt)} · {research.status === 'complete' ? 'Research complete' : 'Partial research'}</p>
      {command.parameters.result.notice && <p>{String(command.parameters.result.notice)}</p>}
      <ReactMarkdown>{research.brief}</ReactMarkdown>
      <div className="mt-3 grid grid-cols-1 gap-3" style={MESSAGE_TEXT}>
        {research.references.map((ref) => <figure key={ref.assetUrl}>
          <a href={ref.sourceUrl} target="_blank" rel="noopener noreferrer">
            {/* Sized to the picture itself: a full-width object-contain box rounds its empty margins, not the image. */}
            <img src={ref.assetUrl} alt={ref.description} title={ref.description} className="mx-auto block h-auto max-h-64 max-w-full rounded-[20px]" />
          </a>
          {/* Inline colour: the chat-message-content-area link rule in globals.css would paint it blue. */}
          <figcaption className="mt-1 truncate text-center text-xs text-[#999999]"><a href={ref.sourceUrl} target="_blank" rel="noopener noreferrer" title={ref.title} style={{ color: 'inherit', textDecoration: 'none' }}>{hostnameOf(ref.sourceUrl)}</a>{ref.publishedDate ? ` · ${formatDate(ref.publishedDate)}` : ''}{ref.market ? ` · ${ref.market}` : ''}</figcaption>
        </figure>)}
      </div>
      <p className="mt-2 text-[#999999]">Guidelines remain the priority. {command.parameters.result.reviewFollows ? 'The review starts now.' : 'Ready when you choose Launch review.'}</p>
    </section>;
  })}</>;
}
